import clsx from 'clsx'
import { useRouter } from 'expo-router'
import { useCallback, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import { ImageCard, type TImageCard } from '@/src/elements/card/image'
import { TEST_ID } from '@/src/general/constants/testId'
import { isLastElement } from '@/src/general/primitives/arrays/isLastElement'
import { isOdd } from '@/src/general/primitives/numbers/isOdd'
import { useTranslate } from '@/src/lang/useTranslate'

import { getGuidesList } from '../../../utils/getGuidesList'

import type { TMessageId } from '@/translations/translations.types'

export type TGuideList = {
  text: TMessageId
  imageSrc: TImageCard['imageSrc']
  navigateTo?: string
}[]

export type TGuideLinksProps = {
  className?: string
}

export const GuideLinks = ({ className }: TGuideLinksProps): ReactNode => {
  const { push } = useRouter()
  const { t } = useTranslate()
  const { locale } = useIntl()

  const guidesList = getGuidesList(locale)

  const calculWidth = useCallback(
    (index: number, array: unknown[]) =>
      isOdd(array.length) && isLastElement(array, index) ? 'basis-full' : 'basis-1/2',
    [],
  )

  const calculPadding = useCallback((index: number, array: unknown[]) => {
    let classNames
    if (isOdd(index)) {
      classNames = 'py-2 pl-2'
    } else if (isLastElement(array, index)) {
      classNames = 'py-2'
    } else {
      classNames = 'py-2 pr-2'
    }
    return classNames
  }, [])

  const handleNavigation = (navigateTo?: string) => {
    if (navigateTo) push(navigateTo)
  }

  return (
    <View testID={TEST_ID.MENU.GUIDES.LIST} className={clsx('flex flex-row flex-wrap', className)}>
      {guidesList.map((guide, index, array) => (
        <View key={guide.text} className={clsx(calculPadding(index, array), calculWidth(index, array))}>
          <ImageCard
            content={t(guide.text)}
            imageSrc={guide.imageSrc}
            className="h-32 hover:cursor-pointer"
            isPressable
            onPress={() => handleNavigation(guide.navigateTo)}
          />
        </View>
      ))}
    </View>
  )
}
